// File: frontend/src/contexts/AuthContext.js

import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { API_CONFIG } from '../config/api';
import { STORAGE_KEYS, TOKEN_REFRESH_BUFFER } from '../config/constants';
import { syncAuthenticatedUserIds, bootstrapAnonymousUser } from '../utils/bootstrap';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const refreshTimerRef = useRef(null);

  useEffect(() => {
    const loadUser = () => {
      try {
        const storedUser = localStorage.getItem(STORAGE_KEYS.AUTH_USER);
        const storedTokens = localStorage.getItem(STORAGE_KEYS.AUTH_TOKENS);
        
        if (storedUser && storedTokens) {
          const userData = JSON.parse(storedUser);
          const tokens = JSON.parse(storedTokens);
          
          if (tokens.expiresAt && Date.now() < tokens.expiresAt) {
            setUser(userData);
            setIsAuthenticated(true);
            setupTokenRefresh(tokens);
          } else {
            attemptTokenRefresh(userData, tokens);
          }
        }
      } catch (error) {
        console.error('Error loading user:', error);
        localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKENS);
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
    
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
    // ✅ FIXED: Added missing dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setupTokenRefresh = (tokens) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    
    const expiresIn = tokens.expiresIn || 3600;
    const refreshIn = (expiresIn - TOKEN_REFRESH_BUFFER) * 1000;
    
    refreshTimerRef.current = setTimeout(() => {
      const storedUser = localStorage.getItem(STORAGE_KEYS.AUTH_USER);
      const storedTokens = localStorage.getItem(STORAGE_KEYS.AUTH_TOKENS);
      
      if (storedUser && storedTokens) {
        const userData = JSON.parse(storedUser);
        const tokenData = JSON.parse(storedTokens);
        attemptTokenRefresh(userData, tokenData);
      }
    }, refreshIn);
  };

  const attemptTokenRefresh = async (userData, tokens) => {
    try {
      if (!tokens.refresh_token) {
        throw new Error('No refresh token available');
      }
      
      const response = await apiService.refreshTokens(
        tokens.refresh_token,
        userData.enterprise_id || API_CONFIG.enterpriseId,
        userData.user_id
      );
      
      if (response.success) {
        const newTokenData = {
          access_token: response.data.tokens.access_token,
          id_token: response.data.tokens.id_token,
          refresh_token: response.data.tokens.refresh_token,
          token_type: response.data.tokens.token_type,
          expiresIn: response.data.tokens.expires_in,
          expiresAt: Date.now() + (response.data.tokens.expires_in * 1000)
        };
        
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKENS, JSON.stringify(newTokenData));
        
        setUser(userData);
        setIsAuthenticated(true);
        setupTokenRefresh(newTokenData);
      } else {
        throw new Error(response.error || 'Token refresh failed');
      }
      
    } catch (error) {
      console.error('Token refresh failed:', error.message);
      
      localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKENS);
      setUser(null);
      setIsAuthenticated(false);
      
      if (window.location.pathname !== '/') {
        navigate('/');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (responseData) => {
    try {
      if (!responseData || !responseData.user || !responseData.tokens) {
        throw new Error('Invalid response data: missing user or tokens');
      }
      
      const { user: userData, tokens } = responseData;
      
      if (!userData.user_id || !userData.email_address) {
        throw new Error('Invalid user data: missing required fields');
      }
      
      if (!tokens.access_token || !tokens.expires_in) {
        throw new Error('Invalid tokens: missing access_token or expires_in');
      }
      
      const userToStore = {
        user_id: userData.user_id,
        cognito_user_sub: userData.cognito_user_sub,
        email_address: userData.email_address,
        phone_number: userData.phone_number,
        first_name: userData.first_name,
        last_name: userData.last_name,
        user_status: userData.user_status,
        user_role: userData.user_role,
        enterprise_id: userData.enterprise_id,
        enterprise_role: userData.enterprise_role,
        primary_language: userData.primary_language
      };
      
      localStorage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(userToStore));
      
      const tokenData = {
        access_token: tokens.access_token,
        id_token: tokens.id_token,
        refresh_token: tokens.refresh_token,
        token_type: tokens.token_type || 'Bearer',
        expiresIn: tokens.expires_in,
        expiresAt: Date.now() + (tokens.expires_in * 1000)
      };
      
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKENS, JSON.stringify(tokenData));
      
      syncAuthenticatedUserIds(userToStore);
      
      setUser(userToStore);
      setIsAuthenticated(true);
      setupTokenRefresh(tokenData);
      
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Error during login:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      
      if (user && user.user_id) {
        try {
          await apiService.signOut(user.user_id);
        } catch (error) {
          console.warn('Backend sign-out failed:', error.message);
        }
      }
      
      localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKENS);
      localStorage.removeItem(STORAGE_KEYS.USER_ID);
      localStorage.removeItem(STORAGE_KEYS.ANONYMOUS_USER_ID);
      localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
      localStorage.removeItem(STORAGE_KEYS.BOOTSTRAP_TIMESTAMP);
      
      await bootstrapAnonymousUser();
      
      setUser(null);
      setIsAuthenticated(false);
      navigate('/');
      
    } catch (error) {
      console.error('Error during logout:', error);
      
      localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKENS);
      localStorage.removeItem(STORAGE_KEYS.USER_ID);
      localStorage.removeItem(STORAGE_KEYS.ANONYMOUS_USER_ID);
      localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
      localStorage.removeItem(STORAGE_KEYS.BOOTSTRAP_TIMESTAMP);
      
      await bootstrapAnonymousUser();
      
      setUser(null);
      setIsAuthenticated(false);
      navigate('/');
    }
  };

  const updateUser = (updates) => {
    try {
      const updatedUser = { ...user, ...updates };
      localStorage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const getAccessToken = () => {
    try {
      const tokens = localStorage.getItem(STORAGE_KEYS.AUTH_TOKENS);
      if (tokens) {
        const tokenData = JSON.parse(tokens);
        if (tokenData.expiresAt && Date.now() < tokenData.expiresAt) {
          return tokenData.access_token;
        }
      }
    } catch (error) {
      console.error('Error getting access token:', error);
    }
    return null;
  };

  const isTokenExpired = () => {
    try {
      const tokens = localStorage.getItem(STORAGE_KEYS.AUTH_TOKENS);
      if (tokens) {
        const tokenData = JSON.parse(tokens);
        return tokenData.expiresAt && Date.now() >= tokenData.expiresAt;
      }
    } catch (error) {
      console.error('Error checking token expiration:', error);
    }
    return true;
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    updateUser,
    getAccessToken,
    isTokenExpired
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
